import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  OnDestroy,
  Renderer2
} from '@angular/core';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { SettingsService } from '@delon/theme';
import { timer } from 'rxjs';
import { take } from 'rxjs/operators';

@Directive({
  selector: '[auto-width-header]',
  standalone: false
})
export class AutoWidthHeaderDirective implements AfterViewInit, OnDestroy {
  private placeholder!: HTMLElement;
  private curUrl: string | undefined = undefined;
  private howToLayoutObserver?: MutationObserver;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private settings: SettingsService,
    private tabSrv: ReuseTabService
  ) {
    this.elementRef.nativeElement.style.position = 'fixed';
    this.elementRef.nativeElement.style['z-index'] = 10;

    this.adjustWidth();

    this.settings.notify.subscribe(() => {
      this.adjustWidth();
    });

    this.tabSrv.change.subscribe(() => {
      if (this.curUrl !== this.tabSrv.curUrl) {
        this.curUrl = this.tabSrv.curUrl;
        this.adjustWidth();
      }
    });
  }

  /** Inset for fixed `app-page-how-to` rail (`PageHowToComponent` sets `--how-to-rail-width` on `documentElement`). */
  private getHowToRailWidthCss(): string {
    if (typeof document === 'undefined') return '0px';
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--how-to-rail-width')
      .trim();
    return raw.length > 0 ? raw : '0px';
  }

  @HostListener('window:resize', ['$event'])
  adjustWidth(): void {
    const collapsed = this.settings.layout.collapsed;
    const rail = this.getHowToRailWidthCss();
    if (this.elementRef) {
      // if browser width is greater than 768px
      if (window.innerWidth >= 768) {
        if (collapsed) {
          this.elementRef.nativeElement.style.width =
            `calc(100vw - 48px - 6px - 64px - ${rail})`;
        } else {
          this.elementRef.nativeElement.style.width =
            `calc(100vw - 48px - 6px - 240px - ${rail})`;
        }
      } else {
        this.elementRef.nativeElement.style.width =
          rail !== '0px' ? `calc(100vw - ${rail})` : '100%';
      }

      // setTimeout(() => {
      if (this.renderer && this.placeholder) {
        timer(0, 500)
          .pipe(take(3))
          .subscribe(() => {
            this.renderer.setStyle(
              this.placeholder,
              'height',
              `${this.elementRef.nativeElement.offsetHeight}px`
            );
          });
      }
      // }, 1000);
    }
  }

  ngAfterViewInit(): void {
    this.placeholder = this.renderer.createElement('div');

    this.renderer.insertBefore(
      this.elementRef.nativeElement.parentNode,
      this.placeholder,
      this.elementRef.nativeElement.nextSibling
    );

    this.adjustWidth();

    if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
      this.howToLayoutObserver = new MutationObserver(() => this.adjustWidth());
      this.howToLayoutObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }
  }

  ngOnDestroy(): void {
    this.howToLayoutObserver?.disconnect();
  }
}
